import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  LayoutDashboard,
  DollarSign,
  MessageSquare,
  ThumbsUp,
  Heart,
  Settings,
  LogOut,
} from "lucide-react";
import { logoutUser } from "../../store/actions/authActions";

const Wrap = styled.div`
  width: 100%;
  @media (min-width: 1024px) {
    width: 18rem;
    flex-shrink: 0;
  }
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Profile = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1.5rem;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Avatar = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
`;

const Name = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
`;

const Since = styled.p`
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const StatBox = styled.div`
  background: hsl(${({ theme }) => theme.colors.muted});
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.75rem;
  text-align: center;
`;

const StatVal = styled.p`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ $accent, theme }) =>
    $accent ? `hsl(${theme.colors.accent})` : `hsl(${theme.colors.primary})`};
`;

const StatLbl = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.625rem;
  font-weight: 500;
  text-transform: uppercase;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Nav = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  overflow: hidden;
`;

const TabList = styled.div`
  & > button:last-of-type {
    border-bottom: none;
  }
`;

const NavBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  background: ${({ $active, theme }) =>
    $active ? `hsl(${theme.colors.primary} / 0.06)` : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? `hsl(${theme.colors.primary})` : `hsl(${theme.colors.foreground})`};
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
  }
`;

const Count = styled.span`
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ $active, theme }) =>
    $active ? `hsl(${theme.colors.primary})` : `hsl(${theme.colors.muted})`};
  color: ${({ $active, theme }) =>
    $active
      ? `hsl(${theme.colors.primaryForeground})`
      : `hsl(${theme.colors.mutedForeground})`};
`;

const SignOut = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-top: 1px solid hsl(${({ theme }) => theme.colors.border});
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.06);
  }
`;

function memberLabel(user) {
  if (!user?.createdAt) return "Member";
  try {
    return `Member since ${new Date(user.createdAt).getFullYear()}`;
  } catch {
    return "Member";
  }
}

export default function DashboardSidebar({ activeTab, onTab }) {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { savings, myComments } = useSelector((s) => s.user);
  const { likedCoupons } = useSelector((s) => s.couponLike);

  const savingsCount = savings.length;
  const commentsCount = myComments.length;
  const likedCount = likedCoupons.length;
  const reviewsCount = 0;

  const totalSaved = savings.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const initial = (user?.name || user?.email || "?").trim().slice(0, 1).toUpperCase();

  const tabs = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "savings", label: "My Savings", icon: DollarSign, count: savingsCount },
    { key: "comments", label: "Comments", icon: MessageSquare, count: commentsCount },
    { key: "reviews", label: "Reviews", icon: ThumbsUp, count: reviewsCount },
    { key: "liked", label: "Liked", icon: Heart, count: likedCount },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <Wrap>
      <Profile>
        <AvatarRow>
          <Avatar>{initial}</Avatar>
          <div>
            <Name>{user?.name || user?.email || "Account"}</Name>
            <Since>{memberLabel(user)}</Since>
          </div>
        </AvatarRow>
        <StatGrid>
          <StatBox>
            <StatVal>${totalSaved.toFixed(0)}</StatVal>
            <StatLbl>Total Saved</StatLbl>
          </StatBox>
          <StatBox>
            <StatVal $accent>{savingsCount}</StatVal>
            <StatLbl>Entries</StatLbl>
          </StatBox>
        </StatGrid>
      </Profile>
      <Nav>
        <TabList>
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavBtn
                key={t.key}
                type="button"
                $active={activeTab === t.key}
                onClick={() => onTab(t.key)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <Icon size={16} strokeWidth={2} />
                  {t.label}
                </span>
                {t.count !== undefined && (
                  <Count $active={activeTab === t.key}>{t.count}</Count>
                )}
              </NavBtn>
            );
          })}
        </TabList>
        <SignOut type="button" onClick={() => dispatch(logoutUser())}>
          <LogOut size={16} /> Sign Out
        </SignOut>
      </Nav>
    </Wrap>
  );
}
